import { useMutation, useQueryClient } from '@tanstack/react-query';
import { diContainer } from 'src/inversify.config';
import CartRepository from 'src/features/cart/data/CartRepository';
import {
  AddToCartParams,
  CreateUserCartParams,
  MigrateCartProductsParams,
  RemoveFromCartParams,
} from 'features/cart/domain/CartRepositoryInterface';
import { Cart, ProductInfo } from 'src/types/common';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';

const cartRepository = diContainer.get<CartRepository>(CartRepository);

export default function useCartMutations() {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const userId = user?.pk as number;

  const addToCart = useMutation({
    mutationFn: (params: AddToCartParams & { product: ProductInfo }) =>
      cartRepository.addToCart(params),
    mutationKey: ['cart', 'add'],
    onMutate: async ({ accessToken, productId, quantity, product, price }) => {
      const queryKey = ['cart', { accessToken, userId }];
      await queryClient.cancelQueries({
        queryKey,
      });

      const previousCart = queryClient.getQueryData<Cart>(queryKey);
      const previousTotalQuantity =
        previousCart?.products.find(
          (cartProduct) => cartProduct.product.pk === productId
        )?.quantity || 0;

      queryClient.setQueryData<Cart>(queryKey, (oldData): Cart => {
        if (oldData) {
          let newProducts = [...oldData.products];
          const productIndex = oldData.products.findIndex(
            (cartProduct) => cartProduct.product.pk === productId
          );

          const productIsInCart = productIndex !== -1;

          if (productIsInCart) {
            newProducts = oldData.products.map((cartProduct) => {
              if (cartProduct.product.pk === productId) {
                return {
                  ...cartProduct,
                  quantity: quantity,
                };
              }

              return cartProduct;
            });
          } else {
            newProducts = [
              ...newProducts,
              { price: price.toString(), product, quantity },
            ];
          }

          return {
            ...oldData,
            total_quantity:
              oldData.total_quantity - previousTotalQuantity + quantity,
            products: newProducts,
          };
        }

        return {} as Cart;
      });

      return { previousCart };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
    onError: (_error: Error, { accessToken }, context) => {
      const queryKey = ['cart', { accessToken, userId }];
      queryClient.setQueryData<Cart>(queryKey, context?.previousCart);
    },
  });
  const removeFromCart = useMutation({
    mutationFn: (params: RemoveFromCartParams) =>
      cartRepository.removeFromCart(params),
    mutationKey: ['cart', 'remove'],
    onMutate: async ({ accessToken, productId }) => {
      const queryKey = ['cart', { accessToken, userId }];
      await queryClient.cancelQueries({
        queryKey,
      });

      const previousCart = queryClient.getQueryData<Cart>(queryKey);
      const previousTotalQuantity =
        previousCart?.products.find(
          (cartProduct) => cartProduct.product.pk === productId
        )?.quantity || 0;

      queryClient.setQueryData<Cart>(queryKey, (oldData): Cart => {
        if (oldData) {
          return {
            ...oldData,
            total_quantity: oldData.total_quantity - previousTotalQuantity,
            products: oldData.products.filter(
              (cartProduct) => cartProduct.product.pk !== productId
            ),
          };
        }

        return {} as Cart;
      });

      return { previousCart };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
    onError: (_error: Error, { accessToken }, context) => {
      const queryKey = ['cart', { accessToken, userId }];
      queryClient.setQueryData<Cart>(queryKey, context?.previousCart);
    },
  });

  const createCart = useMutation({
    mutationFn: (params: CreateUserCartParams) =>
      cartRepository.createUserCart(params),
    mutationKey: ['cart', 'create'],
  });

  const migrateCartProducts = useMutation({
    mutationFn: (params: MigrateCartProductsParams) =>
      cartRepository.migrateCartProducts(params),
    mutationKey: ['cart', 'migrate'],
  });

  return {
    addToCart,
    removeFromCart,
    createCart,
    migrateCartProducts,
  };
}
